import { transcriptCorrections } from "@rosc/contracts";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import type { NoteTemplate } from "@/common/types/notesTemplates";
import { useSidebarRailPageDefault } from "@/components/layout/main/AppSidebar/rail";
import { EmrPastePanel } from "@/components/ui/guided/EmrPastePanel";
import { PracticeNoteBanner } from "@/components/ui/guided/PracticeNoteBanner";
import { usePracticeNote } from "@/components/ui/guided/usePracticeNote";
import type { AssistantRequest } from "@/components/ui/visits/NoteAssistantPanel";
import { spotlight } from "@/components/ui/whats-new/actions";
import { useVisitReads } from "@/hooks/visits/VisitReads";
import { documentTemplateName } from "@/lib/document-name";
import { useFeature } from "@/lib/early-access";
import { trackOnboarding } from "@/lib/guided/events";
import { isPreChartDraft } from "@/lib/pre-chart";
import type { ReadingLevel } from "@/lib/reading-level";
import type { SummaryLanguage } from "@/lib/summary-language";
import { correctionInstruction } from "@/lib/transcript-edit";
import { countVerifyMarkers } from "@/lib/verifyMarkers";
import type { VisitRecording } from "@/lib/visit-content-queries";
import type { FileData } from "@/services/files";
import {
  completeChecklistItem,
  isGuidedChecklistOpen,
} from "@/store/guidedChecklist.store";
import { useWhatsNewStore } from "@/store/whatsNew.store";
import { copyNoteToClipboard } from "../components/copyNote";
import { isNoteGenerating } from "../components/NoteGenerating";
import { FileLatestVersion } from "../components/VisitVersionSelect";
import { useAssistantSurface, usePhoneLayout } from "./layout";
import type { VerifyReviewRequest } from "./NoteDocument";
import { useWorkspaceSources } from "./sources/useVisitSources";
import { TranscriptBar } from "./TranscriptBar";
import { TranscriptChangedBar } from "./TranscriptChangedBar";
import type { VerifyReviewReason } from "./VerifyReview";
import { VisitWorkspaceShell } from "./VisitWorkspaceShell";
import {
  type DocumentControls,
  DocumentControlsProvider,
} from "./v3/DocumentControls";
import { VisitWorkspaceV3 } from "./v3/VisitWorkspaceV3";
import { WorkspaceAssistant } from "./WorkspaceAssistant";
import {
  assistantUnavailableFor,
  MAIN_DOCUMENT_KEY,
  noteDocumentKey,
  WorkspaceDocuments,
} from "./WorkspaceDocuments";
import { WorkspaceCreate, WorkspaceHeader } from "./WorkspaceHeader";

interface Props {
  file: FileData | null;
  notes: NoteTemplate[];
  multiRecordingFiles: VisitRecording[];
  title: string;
  created: string | null;
  notices: React.ReactNode;
  noteGenerationBlocked: boolean;
  expandedItems: string[];
  toggleExpanded: (id: string) => void;
  refresh: () => void;
  onEditTitleClick: () => void;
  // With assistant upload, the selected visit sources go with the request.
  onGenerateReferralLetter: (sourceIds?: readonly number[]) => void;
  // Create → Doctor's note (opens its dialog).
  onGenerateDoctorNote?: () => void;
  onGeneratePatientSummary: (
    sourceIds: readonly number[] | undefined,
    readingLevel: ReadingLevel,
    language: SummaryLanguage,
  ) => void;
  onAddRecording?: () => void;
  // A pre-chart draft: Start visit records it; Save draft returns to the list.
  onStartVisit?: () => void;
  onSaveDraft?: () => void;
  // v3.2 header: rename in place and Delete in "…".
  onRename?: (title: string) => Promise<boolean>;
  onDelete?: () => void;
  deleting?: boolean;
}

// Opens a document that appears after the page loaded (a new referral letter
// or patient summary), so the provider sees what they just generated.
const useActiveDocument = (notes: NoteTemplate[]) => {
  const [activeKey, setActiveKey] = useState(MAIN_DOCUMENT_KEY);
  const known = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = new Set(notes.map((note) => note.public_id));
    if (known.current) {
      const added = notes.filter((note) => !known.current?.has(note.public_id));
      const newest = added[added.length - 1];
      if (newest) setActiveKey(noteDocumentKey(newest));
    }
    known.current = ids;
  }, [notes]);
  const exists =
    activeKey === MAIN_DOCUMENT_KEY ||
    notes.some((note) => noteDocumentKey(note) === activeKey);
  return [exists ? activeKey : MAIN_DOCUMENT_KEY, setActiveKey] as const;
};

export const VisitWorkspace = ({
  file,
  notes,
  multiRecordingFiles,
  title,
  created,
  notices,
  noteGenerationBlocked,
  expandedItems,
  toggleExpanded,
  refresh,
  onEditTitleClick,
  onGenerateReferralLetter,
  onGenerateDoctorNote,
  onGeneratePatientSummary,
  onAddRecording,
  onStartVisit,
  onSaveDraft,
  onRename,
  onDelete,
  deleting,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const surface = useAssistantSurface();
  // 1024–1279px: the app menu starts as a rail (unless the provider chose),
  // so the note keeps a readable width next to the assistant column.
  useSidebarRailPageDefault("compact");
  // Phones: the transcript is a bar docked at the bottom of the screen.
  const transcriptDocked = usePhoneLayout();
  const { selectedVersion, setVersionId, fileVersion, versionLoading } =
    useVisitReads();
  const [activeKey, setActiveKey] = useActiveDocument(notes);
  // The open document's controls (v3.2): its "[verify]" review gate, so
  // Copy to chart copies the document on screen through its own review.
  const documentControls = useRef<DocumentControls | null>(null);
  // Patient summaries and referral letters use the clinician's note: while it
  // has unresolved "[verify]" values, Create opens the main note's review
  // step first (clinical lead, 22/10).
  const [reviewRequest, setReviewRequest] =
    useState<VerifyReviewRequest | null>(null);
  const takeReviewRequest = useCallback(() => {
    setReviewRequest(null);
  }, []);
  // Runs an action on the latest main note once its "[verify]" values are
  // resolved: at once when there are none, else through the note's review.
  const withReviewedNote = (
    reason: VerifyReviewReason,
    label: string,
    proceed: (text: string) => void,
  ): void => {
    if (!file || !countVerifyMarkers(file.result)) {
      proceed(file?.result ?? "");
      return;
    }
    setActiveKey(MAIN_DOCUMENT_KEY);
    if (selectedVersion !== FileLatestVersion) setVersionId(FileLatestVersion);
    setReviewRequest({ key: Date.now(), action: { reason, label, proceed } });
  };
  const afterReview = (
    reason: Extract<VerifyReviewReason, "summary" | "letter">,
    create: () => void,
  ): void => {
    withReviewedNote(
      reason,
      reason === "summary"
        ? t("visit.workspace.verify.review.createSummary")
        : t("visit.workspace.verify.review.createLetter"),
      () => {
        create();
      },
    );
  };
  // v3.2 (VITE_APP_REDESIGN_ENABLED): one "Copy to chart" in the header
  // copies the whole clinical note through the same review, as the iOS app's
  // Copy does (also with Shift+Cmd/Ctrl+C). Section copy stays on each
  // section; letters and summaries keep their own Copy.
  const copyInHeader = AppConfig.enableAppRedesign;
  // Copy to chart copies the document the clinician is looking at: another
  // note of the visit (a second generation, a letter, a summary) or an
  // older version of the clinical note, not always the latest clinical note
  // (clinical lead, 30/10).
  const shownNote = notes.find((note) => noteDocumentKey(note) === activeKey);
  const viewingOldVersion = selectedVersion !== FileLatestVersion;
  const shownText = shownNote
    ? shownNote.result
    : viewingOldVersion
      ? versionLoading
        ? null
        : fileVersion?.result
      : file?.result;
  const canCopyNote =
    !!file &&
    !!shownText?.trim() &&
    (!!shownNote || viewingOldVersion || !isNoteGenerating(file));
  // Guided first visit: a practice consulta (flag guaranteed, guided tour)
  // and, after copying it or a first note, how to paste into the EMR.
  const practiceNote = usePracticeNote(file, refresh);
  const guided = useFeature("guided-first-visit");
  const [emrPanelOpen, setEmrPanelOpen] = useState(false);
  const afterCopy = (): void => {
    if (!guided) return;
    const showPanel = practiceNote.practice || isGuidedChecklistOpen();
    trackOnboarding("note_copied", { practice: practiceNote.practice });
    completeChecklistItem("copy");
    if (showPanel) {
      practiceNote.closeTour();
      setEmrPanelOpen(true);
      trackOnboarding("emr_panel_shown");
    }
  };
  const copyToChart = (): void => {
    if (!canCopyNote) return;
    const label = t("visit.workspace.copyToChart");
    const proceed = (text: string): void => {
      void Promise.resolve(
        copyNoteToClipboard(text, t("visit.workspace.noteCopied")),
      ).then((copied) => {
        if (copied) afterCopy();
      });
    };
    // The latest clinical note: through its review (as Create does).
    if (!shownNote && !viewingOldVersion) {
      withReviewedNote("copy", label, proceed);
      return;
    }
    // Another document or an older version: through that document's own
    // review, on its own text.
    const gate = documentControls.current?.gate;
    if (gate) gate.request({ reason: "copy", label, proceed });
    else proceed(shownText ?? "");
  };
  const copyShortcut = useRef(copyToChart);
  copyShortcut.current = copyToChart;
  useEffect(() => {
    if (!copyInHeader) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === "c"
      ) {
        event.preventDefault();
        copyShortcut.current();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [copyInHeader]);
  const [editing, setEditing] = useState(false);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  // The last saved transcript correction, until the clinician asks for the
  // matching note update or dismisses it.
  const [transcriptChange, setTranscriptChange] = useState<{
    visitPublicId: string;
    before: string;
    after: string;
  } | null>(null);
  const [assistantRequest, setAssistantRequest] =
    useState<AssistantRequest | null>(null);
  const takeAssistantRequest = useCallback(() => {
    setAssistantRequest(null);
  }, []);
  const sources = useWorkspaceSources(file?.public_id ?? "");
  const sourceIds = sources.selectedIds;
  // v3.2: the assistant opens when the clinician asks (Ask bar), so the
  // note gets the width.
  const v3 = AppConfig.enableAppRedesign;
  const [v3AssistantOpen, setV3AssistantOpen] = useState(false);
  const { setOpen: setSurfaceOpen } = surface;
  const setOpen = useCallback(
    (open: boolean) => {
      if (v3) setV3AssistantOpen(open);
      else setSurfaceOpen(open);
    },
    [v3, setSurfaceOpen],
  );
  const openAssistant = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const closeAssistant = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // "Try it" from What's new: open the assistant, or point to the upload
  // area or the Create menu, once the visit has loaded.
  const whatsNewIntent = useWhatsNewStore((state) => state.intent);
  const visitLoaded = !!file;
  useEffect(() => {
    if (!visitLoaded || !whatsNewIntent) return;
    const { takeIntent } = useWhatsNewStore.getState();
    if (takeIntent("assistant")) {
      setOpen(true);
    } else if (takeIntent("upload")) {
      setOpen(true);
      spotlight('[data-testid="source-attach"]', { focus: false });
    } else if (takeIntent("create")) {
      spotlight('[data-testid="create-menu"]');
    }
  }, [visitLoaded, whatsNewIntent, setOpen]);

  const activeNote = shownNote;
  const isMain = !activeNote;
  const documentName =
    (isMain
      ? file && documentTemplateName(file)
      : documentTemplateName(activeNote)) || t("visit.workspace.untitledNote");
  const unavailable = !file
    ? "no-text"
    : assistantUnavailableFor({
        result: isMain ? file.result : activeNote.result,
        editing,
        generating: isMain && isNoteGenerating(file),
        oldVersion: isMain && selectedVersion !== FileLatestVersion,
      });
  const target = {
    visitPublicId: file?.public_id ?? "",
    notePublicId: activeNote ? activeNote.public_id : null,
  };
  const corrections = useMemo(
    () =>
      transcriptChange && transcriptChange.visitPublicId === file?.public_id
        ? transcriptCorrections(transcriptChange.before, transcriptChange.after)
        : [],
    [transcriptChange, file?.public_id],
  );
  const canEditTranscript =
    AppConfig.enableTranscriptEdit &&
    !!file &&
    !!file.transcription?.trim() &&
    !isNoteGenerating(file) &&
    selectedVersion === FileLatestVersion;
  // A note to update: the main note exists and is not being written.
  const showTranscriptChange =
    corrections.length > 0 && !!file?.result?.trim() && !isNoteGenerating(file);
  const updateNoteFromTranscript = (): void => {
    const { instruction, omitted } = correctionInstruction(t, corrections);
    setActiveKey(MAIN_DOCUMENT_KEY);
    if (selectedVersion !== FileLatestVersion) setVersionId(FileLatestVersion);
    setAssistantRequest({
      key: Date.now(),
      instruction,
      origin: "transcript-edit",
      changes: corrections.length - omitted,
      omitted,
    });
    setTranscriptChange(null);
    setOpen(true);
  };
  // The note toolbar opens the assistant when it is not already on screen.
  const showAssistantButton = surface.placement !== "panel" || !surface.open;

  const sourcesProps = file
    ? {
        failed: sources.failed,
        isIncluded: sources.isIncluded,
        loading: sources.loading,
        sources: sources.sources,
        onIncludedChange: sources.setIncluded,
        onRemove: sources.remove,
        onRetryList: sources.retry,
        onUpload: sources.create,
      }
    : undefined;
  const generatePatientSummary = (
    readingLevel: ReadingLevel,
    language: SummaryLanguage,
  ): void => {
    afterReview("summary", () => {
      onGeneratePatientSummary(sourceIds, readingLevel, language);
    });
  };
  const generateReferralLetter = (): void => {
    afterReview("letter", () => {
      onGenerateReferralLetter(sourceIds);
    });
  };
  // A doctor's note reads the clinician's note too: the same review first.
  const generateDoctorNote = onGenerateDoctorNote
    ? (): void => {
        afterReview("letter", onGenerateDoctorNote);
      }
    : undefined;

  const preChart =
    file && isPreChartDraft(file.status) && onStartVisit && onSaveDraft
      ? {
          onStartVisit,
          onSaveDraft,
          startDisabled: multiRecordingFiles.some(
            (recording) =>
              recording.status === "Processing" ||
              recording.status === "Generating",
          ),
        }
      : undefined;

  if (v3) {
    return (
      <DocumentControlsProvider controlsRef={documentControls}>
        <VisitWorkspaceV3
          assistantOpen={v3AssistantOpen}
          copyToChart={{
            onCopy: copyToChart,
            disabled: !canCopyNote,
            empty: !shownText?.trim(),
          }}
          create={
            <WorkspaceCreate
              file={file}
              noteGenerationBlocked={noteGenerationBlocked}
              sourceIds={sourceIds}
              onGeneratePatientSummary={generatePatientSummary}
              onGenerateDoctorNote={generateDoctorNote}
              onGenerateReferralLetter={generateReferralLetter}
            />
          }
          created={created}
          deleting={deleting}
          documentName={documentName}
          documents={
            file && (
              <WorkspaceDocuments
                activeKey={activeKey}
                copyInHeader
                file={file}
                noteGenerationBlocked={noteGenerationBlocked}
                notes={notes}
                reviewRequest={reviewRequest}
                onActiveKeyChange={setActiveKey}
                onEditingChange={setEditing}
                onRegeneratePatientSummary={generatePatientSummary}
                onReviewRequestTaken={takeReviewRequest}
              />
            )
          }
          expandedItems={expandedItems}
          file={file}
          multiRecordingFiles={multiRecordingFiles}
          notices={
            <>
              {practiceNote.practice && (
                <PracticeNoteBanner
                  onRestartTour={
                    practiceNote.ready && !practiceNote.tourOpen
                      ? practiceNote.restartTour
                      : undefined
                  }
                />
              )}
              {notices}
              {showTranscriptChange && (
                <TranscriptChangedBar
                  changes={corrections.length}
                  v3
                  onDismiss={() => {
                    setTranscriptChange(null);
                  }}
                  onUpdateNote={updateNoteFromTranscript}
                />
              )}
            </>
          }
          sourceIds={sourceIds}
          sources={sourcesProps}
          target={target}
          assistantRequest={isMain ? assistantRequest : null}
          canEditTranscript={canEditTranscript}
          title={title}
          toggleExpanded={toggleExpanded}
          unavailable={unavailable}
          onAddRecording={onAddRecording}
          onApplied={refresh}
          onAssistantOpenChange={setV3AssistantOpen}
          onAssistantRequestTaken={takeAssistantRequest}
          onTranscriptSaved={(change) => {
            if (file)
              setTranscriptChange({ visitPublicId: file.public_id, ...change });
          }}
          onDelete={onDelete}
          onRename={onRename}
          preChart={preChart}
          practiceTour={
            practiceNote.practice
              ? {
                  open: practiceNote.tourOpen,
                  reviewed: practiceNote.reviewed,
                  onClose: practiceNote.closeTour,
                }
              : undefined
          }
        />
        {guided && (
          <EmrPastePanel open={emrPanelOpen} onOpenChange={setEmrPanelOpen} />
        )}
      </DocumentControlsProvider>
    );
  }

  return (
    <VisitWorkspaceShell
      notices={
        <>
          {notices}
          {showTranscriptChange && (
            <TranscriptChangedBar
              changes={corrections.length}
              onDismiss={() => {
                setTranscriptChange(null);
              }}
              onUpdateNote={updateNoteFromTranscript}
            />
          )}
        </>
      }
      surface={surface}
      transcriptDocked={transcriptDocked}
      assistant={
        <WorkspaceAssistant
          documentName={documentName}
          target={target}
          unavailable={unavailable}
          sources={
            file
              ? {
                  failed: sources.failed,
                  isIncluded: sources.isIncluded,
                  loading: sources.loading,
                  sources: sources.sources,
                  onIncludedChange: sources.setIncluded,
                  onRemove: sources.remove,
                  onRetryList: sources.retry,
                  onUpload: sources.create,
                }
              : undefined
          }
          sourceIds={sourceIds}
          request={isMain ? assistantRequest : null}
          onRequestTaken={takeAssistantRequest}
          onApplied={refresh}
          onClose={closeAssistant}
          onCollapse={
            surface.placement === "panel" ? closeAssistant : undefined
          }
        />
      }
      header={
        <WorkspaceHeader
          created={created}
          file={file}
          noteGenerationBlocked={noteGenerationBlocked}
          surface={surface}
          title={title}
          copyToChart={
            copyInHeader
              ? { onCopy: copyToChart, disabled: !canCopyNote }
              : undefined
          }
          onEditTitleClick={onEditTitleClick}
          sourceIds={sourceIds}
          onGeneratePatientSummary={(readingLevel, language) => {
            afterReview("summary", () => {
              onGeneratePatientSummary(sourceIds, readingLevel, language);
            });
          }}
          onGenerateDoctorNote={generateDoctorNote}
          onGenerateReferralLetter={() => {
            afterReview("letter", () => {
              onGenerateReferralLetter(sourceIds);
            });
          }}
        />
      }
      note={
        file && (
          <WorkspaceDocuments
            activeKey={activeKey}
            copyInHeader={copyInHeader}
            file={file}
            noteGenerationBlocked={noteGenerationBlocked}
            notes={notes}
            onActiveKeyChange={setActiveKey}
            onEditingChange={setEditing}
            onOpenAssistant={showAssistantButton ? openAssistant : undefined}
            reviewRequest={reviewRequest}
            onReviewRequestTaken={takeReviewRequest}
            onRegeneratePatientSummary={(readingLevel, language) => {
              afterReview("summary", () => {
                onGeneratePatientSummary(sourceIds, readingLevel, language);
              });
            }}
          />
        )
      }
      transcript={
        <TranscriptBar
          canEdit={canEditTranscript}
          docked={transcriptDocked}
          expanded={transcriptExpanded}
          expandedItems={expandedItems}
          file={file}
          multiRecordingFiles={multiRecordingFiles}
          toggleExpanded={toggleExpanded}
          onAddRecording={onAddRecording}
          onExpandedChange={setTranscriptExpanded}
          onTranscriptSaved={(change) => {
            if (file)
              setTranscriptChange({ visitPublicId: file.public_id, ...change });
          }}
        />
      }
    />
  );
};
